/**
 * Referrer API service for ATS.
 */

import { createApiClient } from '@aaas/shared';

export interface Referrer {
  id: string;
  name: string;
  email: string;
  created_at: string;
  updated_at: string;
}

interface ReferrerListResponse {
  items: Referrer[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

interface ReferrerResponse {
  success: boolean;
  data: ReferrerListResponse;
  message: string;
}

interface ListReferrersOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class ReferrerService {
  /**
   * Create a new referrer.
   */
  async createReferrer(
    _workspaceId: string,
    idToken: string,
    name: string,
    email: string
  ): Promise<Referrer> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{ success: boolean; data: Referrer; message: string }>(
      `/referrers`,
      { name, email }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create referrer');
    }

    return response.data.data;
  }

  /**
   * List referrers with pagination.
   */
  async listReferrers(
    _workspaceId: string,
    idToken: string,
    options: ListReferrersOptions = {}
  ): Promise<ReferrerResponse> {
    const { page = 1, size = 100, sortBy = 'name', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<ReferrerResponse>(`/referrers`, {
      params: { skip, limit: size, sort_by: sortBy, order, search: search || undefined },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list referrers');
    }

    return response.data;
  }
}

export default new ReferrerService();
